import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Derive two privacy-preserving identifiers and send them for participant counting.
 *
 *   visitor_id  — 32 random bytes, stored in localStorage["visitor_id"] (permanent per browser)
 *   total_id    — SHA-256(visitor_id + nonce)       — same for the entire reset period
 *   today_id    — SHA-256(visitor_id + nonce + date) — changes each calendar day
 *
 * The nonce comes from the server (GET /api/ideas?page=1) and changes on each admin reset.
 * A new nonce invalidates both IDs, so post-reset browsers count fresh toward Today and Total.
 * A new day produces a new today_id (date changes) but the same total_id (nonce unchanged),
 * so returning browsers count again toward Today but not toward Total.
 */
async function trackUniqueVisit(nonce) {
  if (!nonce) return;
  try {
    let visitorId = localStorage.getItem("visitor_id");
    if (!visitorId) {
      const bytes = new Uint8Array(32);
      window.crypto.getRandomValues(bytes);
      visitorId = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
      localStorage.setItem("visitor_id", visitorId);
    }
    const today = new Date().toISOString().split("T")[0]; // UTC "YYYY-MM-DD"
    const hash = async (s) => {
      const buf = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(s));
      return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, "0")).join("");
    };
    const total_id = await hash(visitorId + nonce);
    const today_id = await hash(visitorId + nonce + today);
    await axios.post(`${API}/visit`, { total_id, today_id });
  } catch {
    // Non-critical
  }
}

// Pixel art fat orange tabby — already on a pale yellow background matching the site
const ANIMAL_URL =
  "https://customer-assets-jt897jd0.emergentagent.net/job_would-help/artifacts/7fc16y49_ChatGPT%20Image%20Sep%2021%2C%202026%2C%2006_24_24%20PM.png";

/* ── Plain 1998-style design tokens ── */
const S = {
  page: {
    backgroundColor: "#FFF4B8",
    minHeight: "100vh",
    fontFamily: "Arial, sans-serif",
    color: "#111",
    fontSize: "15px",
    lineHeight: "1.5",
  },
  wrap: {
    maxWidth: "820px",
    margin: "0 auto",
    padding: "28px 20px 60px",
  },
  headerRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
    marginBottom: "6px",
  },
  h1: {
    fontFamily: "Arial, sans-serif",
    fontWeight: "bold",
    fontSize: "26px",
    lineHeight: "1.3",
    margin: 0,
    color: "#000",
    flex: 1,
  },
  animalImg: {
    width: "90px",
    height: "auto",
    flexShrink: 0,
    marginTop: "2px",
    opacity: 0.92,
  },
  hr: {
    border: "none",
    borderTop: "1px solid #bbb",
    margin: "18px 0",
  },
  hrIdea: {
    border: "none",
    borderTop: "1px solid #ccc",
    margin: "16px 0",
  },
  label: {
    display: "block",
    fontFamily: "Arial, sans-serif",
    fontWeight: "bold",
    fontSize: "15px",
    marginBottom: "6px",
  },
  textarea: {
    display: "block",
    width: "100%",
    minHeight: "90px",
    border: "1px solid #999",
    borderRadius: "2px",
    padding: "8px",
    fontFamily: "Arial, sans-serif",
    fontSize: "15px",
    resize: "vertical",
    backgroundColor: "#fff",
    color: "#111",
    lineHeight: "1.5",
  },
  textareaOver: {
    borderColor: "#cc0000",
  },
  formBottom: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "6px",
  },
  counter: {
    fontFamily: "Arial, sans-serif",
    fontSize: "12px",
    color: "#777",
  },
  counterOver: {
    color: "#cc0000",
    fontWeight: "bold",
  },
  btn: {
    padding: "4px 14px",
    fontFamily: "Arial, sans-serif",
    fontSize: "14px",
    cursor: "pointer",
    backgroundColor: "#ececec",
    border: "2px outset #ccc",
    color: "#000",
    borderRadius: "2px",
  },
  btnDisabled: {
    opacity: 0.5,
    cursor: "default",
  },
  helpBtnHelped: {
    border: "2px inset #ccc",
    backgroundColor: "#d0d0d0",
  },
  statusMsg: {
    fontFamily: "Arial, sans-serif",
    fontSize: "13px",
    marginTop: "8px",
  },
  ideaText: {
    fontFamily: "Arial, sans-serif",
    fontSize: "16px",
    margin: "0 0 5px",
    lineHeight: "1.5",
    color: "#000",
  },
  helpLine: {
    fontFamily: "Arial, sans-serif",
    fontSize: "13px",
    color: "#555",
    margin: "0 0 7px",
  },
  helpBtn: {
    padding: "3px 10px",
    fontFamily: "Arial, sans-serif",
    fontSize: "13px",
    cursor: "pointer",
    backgroundColor: "#ececec",
    border: "2px outset #ccc",
    color: "#000",
    borderRadius: "2px",
  },
  muted: {
    fontFamily: "Arial, sans-serif",
    color: "#666",
    fontSize: "14px",
  },
  typeTab: {
    padding: "4px 12px",
    fontFamily: "Arial, sans-serif",
    fontSize: "13px",
    cursor: "pointer",
    backgroundColor: "#ececec",
    border: "2px outset #ccc",
    color: "#000",
    borderRadius: "2px",
  },
  typeTabActive: {
    border: "2px inset #ccc",
    backgroundColor: "#d0d0d0",
  },
};

const STATUS_MESSAGES = {
  success:    "Your idea has been added.",
  rate_limit: "You've submitted too many ideas recently. Please try again later.",
  error:      "Something went wrong. Please try again.",
};

/* ── Status message for submission forms ── */
function StatusMsg({ status }) {
  if (!status) return null;
  if (status === "blocked") {
    return (
      <div data-testid="moderation-rejection-message" style={{ marginTop: "8px" }}>
        <p style={{ fontFamily: "Arial", fontSize: "13px", color: "#cc0000", margin: "0 0 4px" }}>
          This might contain personal information or content that isn&rsquo;t allowed here. Please edit and try again.
        </p>
        <p style={{ fontFamily: "Arial", fontSize: "13px", color: "#cc0000", margin: "0 0 4px" }}>
          Posts may be rejected for things like threats or violence, targeted harassment, hate directed at a group, theft or property destruction, fraud, stalking or surveillance, or personal/contact information.
        </p>
        <p style={{ fontFamily: "Arial", fontSize: "13px", margin: 0 }}>
          <Link to="/rules" style={{ color: "blue", textDecoration: "underline" }}>See the rules</Link>
        </p>
      </div>
    );
  }
  return (
    <p style={{ fontFamily: "Arial", fontSize: "13px", marginTop: "8px", color: status === "success" ? "#333" : "#cc0000" }}>
      {STATUS_MESSAGES[status]}
    </p>
  );
}

/* ── Individual idea entry ── */
function IdeaEntry({ idea, sitePaused, onDelete }) {
  const storageKey = `helped_${idea.id}`;
  const reportKey  = `reported_${idea.id}`;
  const deleteKey  = `delete_token_${idea.id}`;
  const [helped, setHelped] = useState(
    () => localStorage.getItem(storageKey) === "1"
  );
  const [isReported, setIsReported] = useState(
    () => localStorage.getItem(reportKey) === "1"
  );
  const [canDelete] = useState(
    () => !!localStorage.getItem(deleteKey)
  );
  const [count, setCount] = useState(idea.help_count);
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (helped) {
        const res = await axios.delete(`${API}/ideas/${idea.id}/help`);
        setCount(res.data.help_count);
        localStorage.removeItem(storageKey);
        setHelped(false);
      } else {
        const res = await axios.post(`${API}/ideas/${idea.id}/help`);
        setCount(res.data.help_count);
        localStorage.setItem(storageKey, "1");
        setHelped(true);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const report = async () => {
    if (isReported) return;
    try {
      await axios.post(`${API}/ideas/${idea.id}/report`);
      localStorage.setItem(reportKey, "1");
      setIsReported(true);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDelete = async () => {
    const token = localStorage.getItem(deleteKey);
    if (!token) return;
    if (!window.confirm("Delete this idea?")) return;
    try {
      await axios.delete(`${API}/ideas/${idea.id}`, { data: { token } });
      localStorage.removeItem(deleteKey);
      onDelete(idea.id);
    } catch (e) {
      console.error(e);
    }
  };

  const dateStr = new Date(idea.created_at).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  const smallLinkStyle = {
    background: "none", border: "none", padding: 0,
    color: "blue", textDecoration: "underline",
    cursor: "pointer", fontFamily: "Arial", fontSize: "11px",
  };

  return (
    <div data-testid="idea-card">
      <p style={{ fontFamily: "Arial", fontSize: "12px", color: "#777", margin: "0 0 3px" }}>
        I would&hellip;
      </p>
      <p data-testid="idea-text" style={S.ideaText}>
        {idea.text}
      </p>
      <p style={S.helpLine}>
        <span data-testid="help-count">{count}</span>{" "}
        would help &nbsp;&middot;&nbsp;{" "}
        <span data-testid="idea-timestamp" style={{ color: "#888" }}>
          {dateStr}
        </span>
      </p>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "8px" }}>
        <button
          data-testid="help-button"
          onClick={toggle}
          disabled={busy || sitePaused}
          style={{
            ...S.helpBtn,
            ...(helped ? S.helpBtnHelped : {}),
            ...(busy ? S.btnDisabled : {}),
          }}
        >
          I would help
        </button>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          {canDelete && (
            <button
              data-testid="delete-idea-button"
              onClick={handleDelete}
              style={smallLinkStyle}
            >
              Delete
            </button>
          )}
          {isReported ? (
            <span data-testid="report-confirmed" style={{ fontFamily: "Arial", fontSize: "11px", color: "#555" }}>
              Reported. Thanks.
            </span>
          ) : (
            <button
              data-testid="report-button"
              onClick={report}
              style={smallLinkStyle}
            >
              Report
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Main page ── */
export default function Home() {
  const [wouldText, setWouldText] = useState("");
  const [ideas, setIdeas] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [wouldSubmitting, setWouldSubmitting] = useState(false);
  const [wouldStatus, setWouldStatus] = useState(null);
  const [sitePaused, setSitePaused] = useState(false);

  const loadIdeas = useCallback(async (p, append = false) => {
    try {
      const res = await axios.get(`${API}/ideas?page=${p}`);
      if (append) {
        setIdeas((prev) => [...prev, ...res.data.ideas]);
      } else {
        setIdeas(res.data.ideas);
      }
      setHasMore(res.data.has_more);
      setPage(p);
      if (res.data.paused !== undefined) setSitePaused(res.data.paused);
      // On initial load, track unique participant using the server's current nonce
      if (p === 1 && res.data.participant_nonce) {
        trackUniqueVisit(res.data.participant_nonce);
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    loadIdeas(1).finally(() => setInitialLoading(false));
  }, [loadIdeas]);

  const doSubmit = async (trimmed, postType, setText, setSubmitting, setStatus) => {
    setSubmitting(true);
    setStatus(null);
    try {
      const res = await axios.post(`${API}/ideas`, { text: trimmed, post_type: postType });
      if (res.data.delete_token) {
        localStorage.setItem(`delete_token_${res.data.id}`, res.data.delete_token);
      }
      const { delete_token: _dt, ...ideaData } = res.data;
      setIdeas((prev) => [ideaData, ...prev]);
      setText("");
      setStatus("success");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "CONTACT_INFO_BLOCKED" || detail === "AI_MODERATION_BLOCKED") setStatus("blocked");
      else if (err.response?.status === 429 || detail === "RATE_LIMITED") setStatus("rate_limit");
      else setStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleWouldSubmit = (e) => {
    e.preventDefault();
    const trimmed = wouldText.trim();
    if (!trimmed || wouldSubmitting) return;
    doSubmit(trimmed, "would", setWouldText, setWouldSubmitting, setWouldStatus);
  };

  const handleShowMore = async () => {
    setLoadingMore(true);
    await loadIdeas(page + 1, true);
    setLoadingMore(false);
  };

  const wouldOver = wouldText.length > 280;

  // ── Paused: replace entire public page with maintenance screen ──────────
  if (!initialLoading && sitePaused) {
    return (
      <div
        data-testid="paused-maintenance-page"
        style={{
          backgroundColor: "#FFF4B8",
          minHeight: "100vh",
          fontFamily: "Arial, sans-serif",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "60px 24px",
          textAlign: "center",
        }}
      >
        <h1 style={{ fontFamily: "Arial", fontWeight: "bold", fontSize: "26px", color: "#111", margin: "0 0 10px", lineHeight: "1.3" }}>
          Site paused for maintenance
        </h1>
        <p style={{ fontFamily: "Arial", fontSize: "16px", color: "#555", margin: "0 0 36px" }}>
          Check back soon!
        </p>
        <img
          src={ANIMAL_URL}
          alt=""
          aria-hidden="true"
          style={{ width: "200px", height: "auto", opacity: 0.95 }}
        />
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.wrap}>

        {/* Header — title left, kitten right (desktop only) */}
        <div data-testid="site-header" style={S.headerRow}>
          <h1 data-testid="site-title" style={S.h1}>
            What would you do if enough people would help?
          </h1>
          <img
            src={ANIMAL_URL}
            alt=""
            aria-hidden="true"
            style={S.animalImg}
            className="hidden sm:block"
          />
        </div>

        <p style={{ fontFamily: "Arial, sans-serif", fontSize: "14px", fontWeight: "normal", color: "#111", margin: "8px 0 0" }}>
          This is a thing for seeing what people might help each other do.
        </p>

        <hr style={S.hr} />

        {/* Mobile-only cat — sits below the heading, above the form */}
        <img
          src={ANIMAL_URL}
          alt=""
          aria-hidden="true"
          className="block sm:hidden"
          style={{
            width: "120px",
            height: "auto",
            opacity: 0.92,
            marginBottom: "16px",
          }}
        />

        {/* WWYD submission form */}
        <form data-testid="idea-input-form" onSubmit={handleWouldSubmit}>
          <label htmlFor="would-input" style={S.label}>
            What would you do if enough people would help?
          </label>
          {sitePaused && (
            <p data-testid="paused-message" style={{ fontFamily: "Arial", fontSize: "13px", color: "#555", margin: "0 0 8px", fontStyle: "italic" }}>
              This site is temporarily paused. You can still browse existing ideas.
            </p>
          )}
          <textarea
            id="would-input"
            data-testid="idea-textarea"
            value={wouldText}
            onChange={(e) => { setWouldText(e.target.value); setWouldStatus(null); }}
            rows={4}
            placeholder="I would..."
            disabled={sitePaused}
            style={{
              ...S.textarea,
              ...(wouldOver ? S.textareaOver : {}),
              ...(sitePaused ? { backgroundColor: "#f5f5f5", color: "#aaa", cursor: "not-allowed" } : {}),
            }}
          />
          <div style={S.formBottom}>
            <button
              data-testid="submit-idea-button"
              type="submit"
              disabled={wouldSubmitting || !wouldText.trim() || wouldOver || sitePaused}
              style={{ ...S.btn, ...(wouldSubmitting || !wouldText.trim() || wouldOver ? S.btnDisabled : {}) }}
            >
              {wouldSubmitting ? "Submitting\u2026" : "Submit anonymously"}
            </button>
            <span data-testid="char-counter" style={{ ...S.counter, ...(wouldOver ? S.counterOver : {}) }}>
              {wouldText.length}&nbsp;/&nbsp;280
            </span>
          </div>
          <StatusMsg status={wouldStatus} />
        </form>

        <hr style={S.hr} />

        {/* Ideas stream */}
        {initialLoading ? (
          <p style={S.muted}>Loading&hellip;</p>
        ) : (
          <div data-testid="ideas-stream">
            {ideas.length === 0 ? (
              <p style={S.muted}>No ideas yet.</p>
            ) : (
              ideas.map((idea) => (
                <div key={idea.id}>
                  <IdeaEntry
                    idea={idea}
                    sitePaused={sitePaused}
                    onDelete={(id) => setIdeas((prev) => prev.filter((i) => i.id !== id))}
                  />
                  <hr style={S.hrIdea} />
                </div>
              ))
            )}

            {hasMore && (
              <button
                data-testid="show-more-button"
                onClick={handleShowMore}
                disabled={loadingMore}
                style={{
                  ...S.btn,
                  ...(loadingMore ? S.btnDisabled : {}),
                }}
              >
                {loadingMore ? "Loading\u2026" : "Show more"}
              </button>
            )}
          </div>
        )}

        {/* Footer */}
        {!initialLoading && (
          <div style={{ marginTop: "48px", paddingTop: "10px", borderTop: "1px solid #ccc" }}>
            <p style={{ fontFamily: "Arial", fontSize: "11px", color: "#555", margin: "0 0 6px" }}>
              Ideas are submitted anonymously by users. Appearing here does not mean an idea has been verified or endorsed by this site.
            </p>
            <p style={{ fontFamily: "Arial", fontSize: "12px", color: "#555", margin: 0 }}>
              <Link to="/rules" style={{ color: "blue", textDecoration: "underline" }}>Rules</Link>
              {" \u00b7 "}
              <Link to="/privacy" style={{ color: "blue", textDecoration: "underline" }}>Privacy</Link>
            </p>
          </div>
        )}

      </div>
    </div>
  );
}
